interface CustomSliderSettings {
  autoplay?: boolean;
  autoplayDelay?: number;
  wheelDebounceTimeout?: number;
  endless?: boolean;
  transitionDuration?: number;
  transitionTimingFunction?: string;
}

class CustomSlider {
  private sliderTrack: HTMLElement | null = null;
  private sliderList: HTMLElement | null = null;
  private sliderItems: NodeListOf<HTMLElement> | null = null;
  private activeItemNumber: number = 0;
  private autoplayInterval: number | null = null;
  private wheelDebounceTimeout: number | null = null;
  private clonesBefore?: HTMLElement[] = [];
  private clonesAfter?: HTMLElement[] = [];
  private settings: CustomSliderSettings = {
    autoplay: true,
    autoplayDelay: 3000,
    wheelDebounceTimeout: 350,
    transitionDuration: 250,
    transitionTimingFunction: "ease-in-out",
    endless: false,
  };

  constructor(sliderTrack: HTMLElement, settings?: CustomSliderSettings) {
    this.sliderTrack = sliderTrack;
    if (!this.sliderTrack) return;
    this.sliderList = this.sliderTrack.querySelector(".custom-slider__list");
    if (!this.sliderList) return;
    this.sliderItems = this.sliderList.querySelectorAll(".custom-slider__item");
    this.settings = {...this.settings, ...settings};
    this.init();
  }



  // sliding methods
  next() {
    if (!this.sliderItems || this.sliderItems.length === 0) return;

    let jump = false;

    if (this.activeItemNumber < this.sliderItems.length - 1) {
      this.activeItemNumber++;
    } else {
      this.activeItemNumber = 0;
      jump = true;
    }
      
    this.updateSlider(jump);
  }
  
  prev() {
    if (!this.sliderItems || this.sliderItems.length === 0) return;

    let jump = false;

    if (this.activeItemNumber > 0) {
      this.activeItemNumber--;
    } else {
      this.activeItemNumber = this.sliderItems.length - 1;
      jump = true;
    }

    this.updateSlider(jump);
  }

  // slider update methods
  private updateSlider(jump: boolean = false) {
    if (!this.sliderItems) return;

    this.sliderItems.forEach((item, index) => {
      if (index === this.activeItemNumber) {
        item.classList.add("active");
      } else {
        item.classList.remove("active");
      }
    });
    // Get offset from slider list left border to activeSlide right border
    if (this.sliderList && this.sliderItems && this.sliderItems.length > 0) {

    const activeItem = this.sliderItems[this.activeItemNumber];
    const listRect = this.sliderList.getBoundingClientRect();
    const activeRect = activeItem.getBoundingClientRect();
    let offset = (activeRect.left - listRect.left);

    if(this.sliderList){
      if (this.settings.endless) {
        this.endlessUpdateFn(jump, offset);
      } else {
        this.sliderList.style.transform = `translateX(-${offset}px)`;
      }
    }

  }
  }

  private endlessUpdateFn(jump: boolean = false, offset: number){
    if (!this.sliderList || !this.sliderItems || this.sliderItems.length === 0) return;

    if (this.activeItemNumber === 0 && this.clonesAfter && this.clonesAfter.length > 0 && jump) {
      // if we switched to first item - scroll to first of clonesAfter items
      const firstCloneAfter = this.clonesAfter[0];
      const firstItem = this.sliderItems[0];
      this.seamlessJumpToItemFromCopy(firstItem, firstCloneAfter);
    } else if (this.activeItemNumber === this.sliderItems.length - 1 && this.clonesBefore && this.clonesBefore.length > 0 && jump) {
      // if we switched to last item - scroll to last of clonesBefore elements
      const lastItem = this.sliderItems[this.sliderItems.length - 1];
      const lastCloneBefore = this.clonesBefore[this.clonesBefore.length - 1];
      this.seamlessJumpToItemFromCopy(lastItem, lastCloneBefore);
    } else  {
      this.sliderList.style.transform = `translateX(-${offset}px)`;
    }
  }

  private seamlessJumpToItemFromCopy(item: HTMLElement, copy: HTMLElement){

    const itemTitle = item.querySelector(".products__item-title");
    const copyTitle = copy.querySelector(".products__item-title");



    if (!this.sliderList || !this.sliderItems || this.sliderItems.length === 0) {
      return;
    } 
    const itemRect = item.getBoundingClientRect();
    const copyRect = copy.getBoundingClientRect();
    const listRect = this.sliderList.getBoundingClientRect();
    const itemOffset = (itemRect.left - listRect.left);
    const copyOffset = (copyRect.left - listRect.left);
    this.sliderList.style.transform = `translateX(-${copyOffset}px)`;
    this.sliderList.style.transition = `transform ${this.settings.transitionDuration}ms ${this.settings.transitionTimingFunction}`;
    setTimeout(() => {
      if (this.sliderList) {
        // remove transition
        this.sliderList.style.transition = 'none';
        // jump to non-clone item
        this.sliderList.style.transform = `translateX(-${itemOffset}px)`;
        
        // bring back transition (with delay to avoid flickering)
        setTimeout(() => {
          if (this.sliderList) {
            this.sliderList.style.transition = `transform ${this.settings.transitionDuration}ms ${this.settings.transitionTimingFunction}`;
          }
        }, 100);
      }
    }, this.settings.transitionDuration);
  }


  private addItemsForEndless() {
    if (!this.sliderList || !this.sliderItems || this.sliderItems.length === 0) return;

    if (this.settings.endless) {
      // Clone all slider items and prepend them before the original items
      this.sliderItems.forEach(item => {
        const clone = item.cloneNode(true) as HTMLElement;
        clone.classList.add('custom-slider__item--clone');
        this.clonesBefore!.push(clone);
      });
      // Insert clones before the first child of sliderList
      this.sliderList.prepend(...this.clonesBefore!);
      // Add two copies of all initial items after them in slider list
      this.sliderItems.forEach(item => {
        const clone1 = item.cloneNode(true) as HTMLElement;
        clone1.classList.add('custom-slider__item--clone');
        this.clonesAfter!.push(clone1);
      });
      this.sliderItems.forEach(item => {
        const clone1 = item.cloneNode(true) as HTMLElement;
        clone1.classList.add('custom-slider__item--clone');
        this.clonesAfter!.push(clone1);
      });
      this.sliderList.append(...this.clonesAfter!);

      const activeItemRect =this.sliderItems[this.activeItemNumber].getBoundingClientRect();
      const listRect = this.sliderList.getBoundingClientRect();
      const activeItemOffset = (activeItemRect.left - listRect.left);
      this.seamlessJumpToItemFromCopy(this.sliderItems[this.activeItemNumber], this.clonesBefore![0]);
      
    }
  }

  // autoplay
  startAutoplay() {
    if (!this.settings.autoplay) return;
    this.autoplayInterval = window.setInterval(() => {
      this.next();
    }, this.settings.autoplayDelay);
  }
  stopAutoplay() {
    if (this.autoplayInterval !== null) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  // events
  private addEvents() {
    if (!this.sliderTrack) return;
    this.sliderTrack.addEventListener("mouseenter", this.onMouseEnter.bind(this));
    this.sliderTrack.addEventListener("mouseleave", this.onMouseLeave.bind(this));
  }
  private onMouseEnter() {
    this.stopAutoplay();
    this.transferMouseWheelToSlider();
  }
  private onMouseLeave() {
    this.startAutoplay();
    this.removeMouseWheelFromSlider();
  }
  private onMouseWheel(e: WheelEvent) {
    e.preventDefault();
    

    if (!this.sliderList || this.wheelDebounceTimeout) return;

    
    const scrollDirectionIsDown = e.deltaY > 0; // true - scroll down, false - scroll up

    if (scrollDirectionIsDown) {
      this.next();
    } else {
      this.prev();
    }
    
    // Set debounce timeout for 1 second
    this.wheelDebounceTimeout = window.setTimeout(() => {
      this.wheelDebounceTimeout = null;
    }, this.settings.wheelDebounceTimeout);
  }
  private transferMouseWheelToSlider() {
    if (!this.sliderList) return;
    this.sliderList.addEventListener("wheel", this.onMouseWheel.bind(this));
  }
  private removeMouseWheelFromSlider() {
    if (!this.sliderList) return;
    this.sliderList.removeEventListener("wheel", this.onMouseWheel.bind(this));
    
    // Clear debounce timeout when mouse leaves
    if (this.wheelDebounceTimeout !== null) {
      clearTimeout(this.wheelDebounceTimeout);
      this.wheelDebounceTimeout = null;
    }
  }

  init() {
    if (this.sliderList) {
      this.sliderList.style.transition = `transform ${this.settings.transitionDuration}ms ease-in-out`;
    }
    if (this.settings.endless) {
      this.clonesBefore = [];
      this.clonesAfter = [];
      this.addItemsForEndless();
    }
    if(this.settings.autoplay) {
      this.startAutoplay();
    }
    this.addEvents();
  }
}

export default CustomSlider